import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Seo } from "../lib/seo";
import { MaskedLine, FadeIn } from "../components/Reveal";
import { Eyebrow } from "../components/atoms";
import { api } from "../lib/api";

// Unsubscribe for the Director's Note. Two honest paths:
//  - one-click token links (future emails) land here via ?done=1 / ?invalid=1
//    after the backend applies the change;
//  - the email form below always returns the same message whether or not the
//    address exists, so it cannot be used to enumerate subscribers.
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const tokenDone = params.get("done") === "1";
  const tokenInvalid = params.get("invalid") === "1";
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await api.post("/directors-note/unsubscribe", { email });
    } catch {
      // identical outcome message regardless — no enumeration
    }
    setSending(false);
    setDone(true);
  };

  return (
    <>
      <Seo title="Unsubscribe | 80 Metrics" description="Unsubscribe from the Director's Note." robots="noindex, nofollow" />
      <section className="bg-charcoal text-ivory" data-testid="unsubscribe-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-28 pb-14 lg:pb-20">
          <FadeIn delay={0.1}>
            <Eyebrow dark>Director’s Note</Eyebrow>
          </FadeIn>
          <h1 className="mt-8 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight max-w-4xl">
            <MaskedLine delay={0.25}>Unsubscribe.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-white" data-testid="unsubscribe-page">
        <div className="max-w-xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          {tokenDone ? (
            <div className="border border-charcoal/15 bg-bone p-10" data-testid="unsubscribe-success">
              <p className="text-xs font-semibold tracking-[0.28em] uppercase text-golddark">Done</p>
              <h2 className="mt-4 font-serif text-3xl font-medium text-charcoal">You’ve been unsubscribed.</h2>
              <p className="mt-4 text-sm text-charcoal/65 leading-relaxed">
                You will no longer receive the Director’s Note. Your record is kept so the preference is honoured.
              </p>
            </div>
          ) : done ? (
            <div className="border border-charcoal/15 bg-bone p-10" data-testid="unsubscribe-success">
              <p className="text-xs font-semibold tracking-[0.28em] uppercase text-golddark">Received</p>
              <h2 className="mt-4 font-serif text-3xl font-medium text-charcoal">If that address is subscribed, it’s now off the list.</h2>
              <p className="mt-4 text-sm text-charcoal/65 leading-relaxed">
                The preference is recorded with a timestamp and honoured by any future send.
              </p>
            </div>
          ) : (
            <>
              {tokenInvalid && (
                <p className="mb-8 border border-gold/50 bg-bone px-5 py-4 text-xs leading-relaxed text-charcoal/75" data-testid="unsubscribe-invalid">
                  This unsubscribe link is invalid or has already been used. You can unsubscribe by email below.
                </p>
              )}
              <p className="text-sm lg:text-base text-charcoal/70 leading-relaxed">
                Enter the email address you subscribed with and we’ll remove it from the Director’s Note list.
              </p>
              <form onSubmit={submit} className="mt-8 flex flex-col gap-4" data-testid="unsubscribe-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  data-testid="unsubscribe-email-input"
                  className="w-full bg-transparent border border-charcoal/25 px-4 py-3.5 text-sm text-charcoal placeholder:text-charcoal/40 outline-none transition-colors duration-300 focus:border-gold"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="unsubscribe-submit"
                  className="px-8 py-4 bg-charcoal text-ivory text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color,color] duration-300 hover:bg-gold hover:text-ink disabled:opacity-60"
                >
                  {sending ? "Updating…" : "Unsubscribe me"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </>
  );
}
